// src/pages/shipping/RateTables.tsx
/* Movatic WMS — Frontend | CesarFilho 932048016
   Lista/CRUD de Tabelas de Frete + detalhes (linhas/sobretaxas).
   React-Query v5 + UI stubs (Button, Input, Label, Dialog*).
*/
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
    Button,
    Input,
    Label,
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui";
import RateRowTable from "./components/RateRowTable";
import SurchargeTable from "./SurchargeTable";

type Carrier = { id: number; name: string; code?: string };
type RateTable = {
    id: number;
    carrier: number;
    name: string;
    zone?: string | null;
    active?: boolean;
    created_at?: string;
};
type Paged<T> = { count: number; next: string | null; previous: string | null; results: T[] };

const PAGE_SIZE = 10;

export default function RateTables() {
    const qc = useQueryClient();
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const [carrier, setCarrier] = useState<number | "">("");
    const [selected, setSelected] = useState<RateTable | null>(null);

    // --- carriers para o combo
    const qCarriers = useQuery<Carrier[]>({
        queryKey: ["carriers"],
        queryFn: async () => {
            const { data } = await api.get("/api/partners/carriers/", { params: { page_size: 1000 } });
            if (Array.isArray(data)) return data as Carrier[];
            return (data?.results ?? []) as Carrier[];
        },
        staleTime: 60_000,
        // tipado para evitar TS7006
        placeholderData: (p: Carrier[] | undefined) => p,
    });

    // --- rate tables
    const qTables = useQuery<Paged<RateTable>>({
        queryKey: ["rate-tables", { page, q, carrier }],
        queryFn: async () => {
            const { data } = await api.get("/api/shipping/rate-tables/", {
                params: { page, page_size: PAGE_SIZE, q: q || undefined, carrier: carrier || undefined },
            });
            if (Array.isArray(data)) {
                return { count: data.length, next: null, previous: null, results: data as RateTable[] };
            }
            return data as Paged<RateTable>;
        },
        // tipado para evitar TS7006
        placeholderData: (p: Paged<RateTable> | undefined) => p,
        staleTime: 10_000,
    });

    useEffect(() => {
        setPage(1);
    }, [q, carrier]);

    const total: number = qTables.data?.count ?? 0;
    const rows: RateTable[] = qTables.data?.results ?? [];
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // ---------- Create / Update / Delete ----------
    const mCreate = useMutation({
        mutationFn: async (payload: Partial<RateTable>) => api.post("/api/shipping/rate-tables/", payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rate-tables"] });
            setOpenCreate(false);
        },
    });
    const mUpdate = useMutation({
        mutationFn: async (rt: RateTable) => api.patch(`/api/shipping/rate-tables/${rt.id}/`, rt),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rate-tables"] });
            qc.invalidateQueries({ queryKey: ["rate-rows"] });
        },
    });
    const mDelete = useMutation({
        mutationFn: async (id: number) => api.delete(`/api/shipping/rate-tables/${id}/`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rate-tables"] });
            setSelected(null);
        },
    });

    const [openCreate, setOpenCreate] = useState(false);

    return (
        <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LISTA */}
            <div className="space-y-3">
                <div className="flex flex-wrap items-end gap-2">
                    <div className="grow min-w-[180px]">
                        <Label>Pesquisar</Label>
                        <Input
                            value={q}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQ(e.target.value)}
                            placeholder="Nome, zona..."
                        />
                    </div>
                    <div>
                        <Label>Transportadora</Label>
                        <select
                            className="block rounded border border-slate-600 bg-slate-900/40 px-3 py-2 text-sm"
                            value={carrier as any}
                            onChange={(e) => setCarrier(e.target.value ? Number(e.target.value) : "")}
                        >
                            <option value="">Todas</option>
                            {(qCarriers.data ?? []).map((c: Carrier) => (
                                <option key={c.id} value={c.id}>
                                    {c.code ? `${c.code} — ${c.name}` : c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="ml-auto">
                        <Label>&nbsp;</Label>
                        <div>
                            <Button onClick={() => setOpenCreate(true)}>+ Nova Tabela</Button>
                        </div>
                    </div>
                </div>

                <div className="overflow-auto border rounded">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-900/30">
                            <tr>
                                <th className="text-left p-2">Nome</th>
                                <th className="text-left p-2">Transportadora</th>
                                <th className="text-left p-2">Zona</th>
                                <th className="text-left p-2">Ativa</th>
                                <th className="text-right p-2">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="p-6 text-center opacity-70">
                                        Nenhum registro.
                                    </td>
                                </tr>
                            )}
                            {rows.map((rt: RateTable) => (
                                <tr key={rt.id} className="border-t border-slate-800">
                                    <td className="p-2">{rt.name}</td>
                                    <td className="p-2">
                                        {qCarriers.data?.find((c: Carrier) => c.id === rt.carrier)?.name ?? rt.carrier}
                                    </td>
                                    <td className="p-2">{rt.zone ?? "—"}</td>
                                    <td className="p-2">{rt.active ? "Sim" : "Não"}</td>
                                    <td className="p-2 text-right">
                                        <div className="inline-flex gap-2">
                                            <Button size="sm" variant="outline" onClick={() => setSelected(rt)}>
                                                Abrir
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="danger"
                                                onClick={() => mDelete.mutate(rt.id)}
                                                disabled={mDelete.isPending}
                                            >
                                                Excluir
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="flex items-center justify-between text-sm">
                    <div>
                        {total ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} de ${total}` : "—"}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                        >
                            Anterior
                        </Button>
                        <div className="px-2 py-1 border rounded">
                            Página {page} / {totalPages}
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page * PAGE_SIZE >= total}
                        >
                            Próxima
                        </Button>
                    </div>
                </div>
            </div>

            {/* DETALHE */}
            <div className="space-y-4">
                {!selected ? (
                    <div className="opacity-70 p-6 border rounded">
                        Selecione uma tabela para ver as linhas e sobretaxas.
                    </div>
                ) : (
                    <>
                        <EditHeader
                            value={selected}
                            carriers={qCarriers.data ?? []}
                            onChange={(v) => setSelected(v)}
                            onSave={() => selected && mUpdate.mutate(selected)}
                            saving={mUpdate.isPending}
                        />
                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                            <div className="border rounded">
                                <div className="px-3 py-2 border-b">Linhas</div>
                                <RateRowTable tableId={selected.id} />
                            </div>
                            <div className="border rounded">
                                <div className="px-3 py-2 border-b">Sobretaxas</div>
                                <SurchargeTable tableId={selected.id} />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Dialog Novo */}
            <Dialog open={openCreate} onOpenChange={setOpenCreate}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Nova Tabela de Frete</DialogTitle>
                    </DialogHeader>
                    <CreateForm
                        carriers={qCarriers.data ?? []}
                        submitting={mCreate.isPending}
                        onSubmit={(payload) => mCreate.mutate(payload)}
                    />
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenCreate(false)}>
                            Cancelar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function EditHeader({
    value,
    carriers,
    onChange,
    onSave,
    saving,
}: {
    value: RateTable;
    carriers: Carrier[];
    onChange: (v: RateTable) => void;
    onSave: () => void;
    saving?: boolean;
}) {
    return (
        <div className="border rounded p-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                    <Label>Nome</Label>
                    <Input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} />
                </div>
                <div>
                    <Label>Transportadora</Label>
                    <select
                        className="block w-full rounded border border-slate-600 bg-slate-900/40 px-3 py-2 text-sm"
                        value={value.carrier}
                        onChange={(e) => onChange({ ...value, carrier: Number(e.target.value) })}
                    >
                        {carriers.map((c: Carrier) => (
                            <option key={c.id} value={c.id}>
                                {c.code ? `${c.code} — ${c.name}` : c.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <Label>Zona</Label>
                    <Input value={value.zone ?? ""} onChange={(e) => onChange({ ...value, zone: e.target.value })} />
                </div>
            </div>
            <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={!!value.active}
                        onChange={(e) => onChange({ ...value, active: e.target.checked })}
                    />
                    Ativa
                </label>
                <div className="ml-auto">
                    <Button onClick={onSave} disabled={saving}>
                        {saving ? "Salvando..." : "Salvar alterações"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function CreateForm({
    carriers,
    submitting,
    onSubmit,
}: {
    carriers: Carrier[];
    submitting?: boolean;
    onSubmit: (payload: Partial<RateTable>) => void;
}) {
    const [f, setF] = useState<Partial<RateTable>>({
        name: "",
        carrier: carriers[0]?.id ?? 0,
        zone: "",
        active: true,
    });

    useEffect(() => {
        if (!f.carrier && carriers.length) setF((s) => ({ ...s, carrier: carriers[0].id }));
    }, [carriers]);

    return (
        <div className="grid gap-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                    <Label>Nome</Label>
                    <Input value={f.name ?? ""} onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} />
                </div>
                <div>
                    <Label>Transportadora</Label>
                    <select
                        className="block w-full rounded border border-slate-600 bg-slate-900/40 px-3 py-2 text-sm"
                        value={f.carrier ?? 0}
                        onChange={(e) => setF((s) => ({ ...s, carrier: Number(e.target.value) }))}
                    >
                        {carriers.map((c: Carrier) => (
                            <option key={c.id} value={c.id}>
                                {c.code ? `${c.code} — ${c.name}` : c.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                    <Label>Zona</Label>
                    <Input value={f.zone ?? ""} onChange={(e) => setF((s) => ({ ...s, zone: e.target.value }))} />
                </div>
                <div className="md:col-span-2 flex items-end">
                    <label className="inline-flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={!!f.active}
                            onChange={(e) => setF((s) => ({ ...s, active: e.target.checked }))}
                        />
                        Ativa
                    </label>
                </div>
            </div>
            <div className="flex justify-end">
                <Button onClick={() => onSubmit(f)} disabled={submitting}>
                    {submitting ? "Salvando..." : "Criar"}
                </Button>
            </div>
        </div>
    );
}
